import React, {useEffect, useState} from "react";
import { Box, Grid, Typography } from "@material-ui/core";
import styled from "styled-components/macro";
import { GenericSidePanel } from "../../../../components/SidePanel/GenericSidePanel.comp";
import MonthRangePicker from "../../../../components/DatePickers/MonthRangePicker.comp";
import { HorizontalBar } from "react-chartjs-2";
import Chart from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {CustomSelect} from "../../../SchoolDashboard/Assessment/components/Select.comp";
import {statusToDisplay, useBarChartData} from "../hooks/useBarChartData";
import {STATUS_COLORS, STATUS_ORDER, STATUS_ORDER_INDEX_TO_COLOR_KEY} from "../../../../utils/constants";

if (Chart.plugins.getAll().findIndex(plugin => plugin.id === 'datalabels') === -1) {
    Chart.plugins.register(ChartDataLabels);
}

const years = ["All Years", "2022", "2023"];

const LegendSquare = styled(Box)`
    width: 110px;
    height: 32px;
    background: ${props => props.color};
    color: ${props => (props.textColor ? props.textColor : "#fff")};
    font-weight: 700;
    font-size: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    @media (min-width: 768px) and (max-width: 1024px) {
        font-size: 9px;
        width: 125px;
    }
    @media (max-width: 768px) {
        font-size: 8px;
    }
`;

const LegendRow = styled(Box)`
    display: flex;
    align-items: center;
`;

const PanelBox = styled(Box)`
    background: #fff;
    border-radius: 8px;
    padding: 18px 18px 16px 18px;
    box-shadow: 0 2px 8px rgba(10,20,40,0.04);
    min-width: 260px;
    margin-bottom: 12px;
`;

function ScoreByGradeLevelTab({byLevelData, assessIndicators, selectedCategory, setSelectedCategory, monthRange, setMonthRange}) {
    const categories = assessIndicators.map(i => i.name);
    const selectedIndicator = assessIndicators.find(i => i.name === selectedCategory);
    useEffect(() => {
        if (!selectedCategory && categories.length > 0)
            setSelectedCategory(categories[0]);
    }, [categories, selectedCategory, setSelectedCategory]);

    const [minDate, maxDate] = [new Date(2022, 0, 1), new Date(2023, 11, 31)];

    const barChartData = useBarChartData({
        byGroupData: byLevelData,
        selectedIndicator,
        groupLabelFormat: g => `${g}`,
    });

    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            xAxes: [{
                stacked: true,
                ticks: { min: 0, max: 100, callback: value => value + "%" },
                scaleLabel: { display: true, labelString: "%" }
            }],
            yAxes: [{ stacked: true }]
        },
        legend: { display: false },
        tooltips: { enabled: true, mode: "index", intersect: false },
        plugins: {
            datalabels: {
                display: true,
                color: ctx => {
                    const bg = ctx.dataset.backgroundColor;
                    if (bg === "#ffb300") return "#222";
                    return "#fff";
                },
                font: { weight: 'bold', size: 15 },
                formatter: (value, context) => value > 0 ? value + "%" : "",
            }
        },
        layout: { padding: { left: 0, right: 0, top: 0, bottom: 0 } },
    };

    const SidePanel = (
        <PanelBox>
            <GenericSidePanel
                data={[categories]}
                headers={["Select a Category"]}
                visible={[true, true]}
                colourize={[false, false]}
                defaultCheckIndices={[0, 0]}
                singleSelectIndices={[0, undefined]}
                onCheckCallbacks={[
                    (categoryName, checked, singleState, setSinglePanelState) => {
                        const newState = Object.fromEntries(
                            Object.keys(singleState).map(name => [name, name === categoryName ? checked : false])
                        );
                        setSinglePanelState(newState);
                        if (checked) setSelectedCategory(categoryName);
                    },
                    undefined
                ]}
                footers={[undefined, undefined]}
                footersData={[undefined, { minDate, maxDate, value: monthRange }]}
                footersCallbacks={[undefined, { onChangeDate: setMonthRange }]}
            />
        </PanelBox>
    );

    return (
        <Box px={2} py={2} width="100%">
            <Grid container spacing={0} alignItems="center" style={{ marginBottom: 16 }}>
                <Grid item xs={12} md={8}>
                    <Box style={{paddingLeft:'30px'}}>
                        <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 6 }}>
                            Performance Distribution by Grade Level
                        </Typography>
                        <Typography style={{ color: "#444", fontSize: 16,paddingRight:'75px' }}>
                            This section displays how student performance vary by grade levels. Reviewing score trends by levels helps educators understand developmental patterns in digital skills and AI readiness, allowing for level-appropriate strategies and tailored educational support.
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 6, textAlign:'center' }}>
                        Rubric
                    </Typography>
                    <Box display="flex" pr={7} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                        {STATUS_ORDER.map((key, index) => {
                            // Get color key from index for blank entries, or use key directly
                            const colorKey = !key || key === "" 
                                ? STATUS_ORDER_INDEX_TO_COLOR_KEY[index] 
                                : key;
                            const color = STATUS_COLORS[colorKey];
                            return (
                                <LegendSquare
                                    key={index}
                                    color={color}
                                    textColor={colorKey === "active" ? "#222" : "#fff"}
                                    style={{ margin: 0, borderRadius: 0 }}
                                >
                                    {key ? statusToDisplay(key) : ""}
                                </LegendSquare>
                            );
                        })}
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={0} style={{marginTop:'30px'}} alignItems="flex-start">
            {/* Side Panel */}
                <Grid item xs={12} md={4} lg={3}>
                    {SidePanel}
                </Grid>
                {/* Chart Section */}
                <Grid item xs={12} md={8} lg={9}>
                    <Box
                        style={{
                            height: 40, // Adjust as needed for spacing
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            fontSize: 18,
                        }}
                    >
                        Percentage of Students in Each Rubric Score by Grade Level
                    </Box>
                    <Box style={{ background: "#f6f6f6", padding: 24, borderRadius: 8, height: 380 }}>
                        <HorizontalBar
                            data={barChartData}
                            options={barChartOptions}
                            height={300}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default ScoreByGradeLevelTab;
